import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  useFormShowContext,
  useFormContext,
} from 'framework/form';
import {
  CYCLE_DAYS_LABELS,
  CYCLE_FREQUENCY_LABELS,
  CYCLE_SETTINGS_STATUS,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import EditCycleSettings from './EditCycleSettings';
import RejectedReason from './RejectedReason';
import { ShowSection } from 'framework/ui/layout';

const CyclesSettings = () => {
  const { isAccessGranted } = useProjectSecurityContext();
  const { isEditMode } = useFormShowContext();
  const { data } = useFormContext();

  const cycleSettings = useMemo(() => {
    return data?.settings?.cycleActivitySettings;
  }, [data?.settings?.cycleActivitySettings]);

  const renderActions = useCallback(() => {
    if (!isAccessGranted(ACCESS_INFO_SECTIONS.SDLC, ACCESS_INFO_ACTIONS.MANAGE)) {
      return null;
    }

    // after negative review
    if (
      isEditMode &&
      (cycleSettings?.cycleSettingsStatus === CYCLE_SETTINGS_STATUS.REJECTED ||
        cycleSettings?.cycleSettingsStatus === CYCLE_SETTINGS_STATUS.IN_APPROVAL)
    ) {
      return [<CloseFormShowButton key="close" />];
    }

    return isEditMode
      ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
      : [<EditFormShowButton key="edit" />];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, data]);

  return (
    <ShowSection
      actions={renderActions()}
      disableDefaultPadding
      sx={{
        justifyContent: 'space-between',
        m: '0 !important',
        paddingBottom: '15px !important',
        borderBottom: '1px solid',
        borderColor: 'border.default',
        '.show-title-container > .MuiGrid2-root': {
          display: 'flex',
          alignItems: 'center',
        },
        '.show-title-container > .MuiGrid2-root > .MuiBox-root': {
          flex: '1 1 auto',
          minWidth: 0,
        },
        '.show-title-container > .MuiGrid2-root > .MuiTypography-root': {
          flex: '1 1 auto',
          minWidth: 0,
        },
        '.section-details__actions': {
          flexGrow: '0 !important',
          flexShrink: 0,
        },
        '.show-title-container': { m: '0 !important' },
        '& .MuiIconButton-root': { m: '0 !important' },
      }}
      title={
        isEditMode ? (
          <Grid size={12} width="100%" m={0}>
            <Typography variant="h4" color="primary">
              Cycles settings
            </Typography>
          </Grid>
        ) : (
          <Grid container spacing={2} size={12} m={0} width="100%" alignItems="center">
            <Grid size={2}>
              <Typography variant="h4" color="primary">
                Cycles
              </Typography>
            </Grid>
            {cycleSettings?.cycleSettingsStatus === CYCLE_SETTINGS_STATUS.REJECTED && (
              <Grid size={10} minWidth={0}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="flex-end"
                  spacing={1}
                  width="100%"
                  minWidth={0}
                >
                  <Typography color="textSecondary" noWrap flexShrink={0}>
                    Rejection reason:
                  </Typography>
                  <RejectedReason reason={cycleSettings?.cycleRejectionReason} />
                </Stack>
              </Grid>
            )}
            {cycleSettings?.cycleSettingsStatus !== CYCLE_SETTINGS_STATUS.IN_APPROVAL &&
              cycleSettings?.cycleSettingsStatus !== CYCLE_SETTINGS_STATUS.REJECTED && (
                <>
                  <Grid size={2.5}>
                    <Typography variant="h4" color="primary">
                      {CYCLE_FREQUENCY_LABELS[cycleSettings?.cycleFrequency]}
                    </Typography>
                  </Grid>
                  <Grid size={2.5}>
                    <Typography color="textSecondary">
                      {CYCLE_DAYS_LABELS[cycleSettings?.cycleStartDay]} -{' '}
                      {CYCLE_DAYS_LABELS[cycleSettings?.cycleEndDay]}
                    </Typography>
                  </Grid>
                  <Grid size={2.5}>
                    <Typography color="textSecondary" noWrap flexShrink={0}>
                      Planning: {CYCLE_DAYS_LABELS[cycleSettings?.planningDay]}
                    </Typography>
                  </Grid>
                  <Grid size={2.5}>
                    <Typography color="textSecondary" noWrap flexShrink={0}>
                      Demo: {CYCLE_DAYS_LABELS[cycleSettings?.demoDay]}
                    </Typography>
                  </Grid>
                </>
              )}
          </Grid>
        )
      }
    >
      {isEditMode ? <EditCycleSettings /> : <></>}
    </ShowSection>
  );
};

export default CyclesSettings;
